import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { cacheGet, cacheSet } from "../lib/offline";

const color = (c) => (c == null ? "#94A3B8" : c >= 80 ? "#059669" : c >= 50 ? "#D97706" : "#DC2626");

/** Compact Leaflet map of health zones (completeness colouring), reusable across pages. */
export function ZoneMap({ province, highlight, height = 300, testid = "zone-map" }) {
  const { t } = useLang();
  const key = `zonemap:${province || "all"}`;
  const { data } = useQuery({
    queryKey: ["zone-map", province],
    retry: false,
    queryFn: async () => {
      try {
        const res = (await api.get("/map-data", { params: province ? { province } : {} })).data;
        await cacheSet(key, res);
        return res;
      } catch (e) {
        const c = await cacheGet(key);
        if (c?.value) return c.value;
        throw e;
      }
    },
  });
  const zones = useMemo(() => (data || []).filter((z) => z.lat && z.lng), [data]);
  const center = useMemo(() => {
    const sel = zones.find((z) => z.health_zone === highlight);
    if (sel) return [sel.lat, sel.lng];
    if (!zones.length) return [-1.8, 29.2];
    return [zones.reduce((a, z) => a + z.lat, 0) / zones.length, zones.reduce((a, z) => a + z.lng, 0) / zones.length];
  }, [zones, highlight]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" style={{ height }} data-testid={testid}>
      <MapContainer key={`${center[0]}-${center[1]}-${highlight || ""}`} center={center} zoom={highlight ? 9 : province ? 7 : 6}
        style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {zones.map((z) => {
          const sel = z.health_zone === highlight;
          const c = z.entry_completeness ?? z.completeness;
          return (
            <CircleMarker key={z.health_zone} center={[z.lat, z.lng]} radius={sel ? 14 : 8}
              pathOptions={{ color: sel ? "#0F172A" : "#fff", weight: sel ? 3 : 1.5, fillColor: color(c), fillOpacity: 0.85 }}>
              <Tooltip direction="top">
                <b>{z.health_zone}</b> · {z.province}<br />
                {t("Saisies")}: {z.reports ?? 0} · {t("Complétude")}: {c == null ? "N/A" : `${c} %`}<br />
                {t("Alertes")}: {z.alerts_received ?? 0} · {t("Cas")}: {z.suspected_cases ?? 0}
              </Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default ZoneMap;
